import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAddDomain } from 'Features/domainsManagement/api/addDomain'
import { useGetErrorText } from 'Utils/errorText'
import { validateDomain } from 'Utils/validation'
import {
  InputWithIcon,
  LoadingIcon,
  StyledText,
  useFrontEndEventTrackingService,
  useTriggerOnPressEnter,
} from '@clickcease/cc-client-components-lib'
import { CSS } from '@stitches/react'
import { useAllowedLimits } from 'src/hooks/useAllowedLimits'
import { trimPrototcolAndWww } from 'Utils/routes'
import {
  AddDomainModalInput,
  AddDomainStyled,
  DomainModalError,
  InputError,
  AddDomainModalButtonContainer,
} from './AddSingleDomain.styled'
import { LargeAuthButton } from 'Components/Elements/LargeAuthButton/LargeAuthButton.styled'
import { isMobile } from 'react-device-detect'
import { useDeleteDomain } from 'Features/domainsManagement/api/deleteDomain'
import { useGetDomains } from 'Features/domainsManagement/api/getDomains'
import { useAppStore } from 'Stores/appStoreModuleFed'

interface AddSingleDomainProps {
  css?: CSS
  onSuccessCallback?: (domainId: number, domain: string, isNoTimeout?: boolean) => void
  addedDomainAddress?: string
  addedDomainId?: number
}

export const AddSingleDomain = ({
  css,
  onSuccessCallback,
  addedDomainAddress,
  addedDomainId,
}: AddSingleDomainProps) => {
  const { t } = useTranslation('onboarding')
  const { track } = useFrontEndEventTrackingService()
  const { mutate: addDomain, isLoading, isError, error } = useAddDomain()
  const { getErrorText } = useGetErrorText()
  const { allowedProducts } = useAllowedLimits()
  const { showToaster } = useAppStore()
  const [domainSubmitted, setDomainSubmitted] = useState<boolean>(false)
  const [inputError, setInputError] = useState<string>('')
  const showInputError = !!inputError.length

  const { mutate: deleteDomain, isLoading: isDeleting } = useDeleteDomain()

  const { data } = useGetDomains()
  const domains = data?.data.domains
  const addedDomain = domains && domains.length > 0 ? domains[0] : undefined
  const existingDomainAddress = addedDomainAddress ? addedDomainAddress : addedDomain?.address
  const existingDomainId = addedDomainId ? addedDomainId : addedDomain?.id

  const [inputValue, setInputValue] = useState(existingDomainAddress ? existingDomainAddress : '')

  const handleDomainInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    setInputError('')
  }

  const validateInput = () => {
    if (!inputValue.length) {
      setInputError(t('add_new_domain_modal.empty_domain', { ns: 'domainManagement' }))
    } else if (!validateDomain(inputValue)) {
      setInputError(t('add_new_domain_modal.invalid_domain', { ns: 'domainManagement' }))
    } else {
      setInputError('')
    }
  }

  const submitDomain = () => {
    setDomainSubmitted(true)
    const cleanAddress = trimPrototcolAndWww(inputValue)
    const validatedDomains = [
      {
        address: cleanAddress,
        ...allowedProducts,
      },
    ]
    addDomain(
      { domains: validatedDomains },
      {
        onSuccess: (res) => {
          if (!(res instanceof Error)) {
            if (onSuccessCallback)
              onSuccessCallback(res.data.addedDomains[0].id, validatedDomains[0].address)
            track(['dataLayer'], 'Onboarding.addedFirstDomain', {
              domain: res.data.addedDomains[0].address,
            })
          } else {
            setDomainSubmitted(false)
          }
        },
      }
    )
  }

  const addNewDomain = () => {
    validateInput()
    if (validateDomain(inputValue)) {
      // if already added domain is the same as the one in input
      if (onSuccessCallback && existingDomainAddress === inputValue && existingDomainId) {
        onSuccessCallback(existingDomainId, existingDomainAddress, true)
      } else if (addedDomainId || addedDomain) {
        // if user are back to chnage first domain
        deleteDomain(
          { domainId: existingDomainId as number },
          {
            onSuccess: () => {
              submitDomain()
              showToaster(
                t('add_domain.domain_deleted', {
                  domainAddress: existingDomainAddress,
                  components: {
                    1: <StyledText css={{ color: 'currentColor' }} variant={'baseRegular'} />,
                  },
                }),
                'info'
              )
            },
          }
        )
      } else {
        // first time adding domain
        submitDomain()
      }
    }
  }

  useTriggerOnPressEnter(addNewDomain)

  return (
    <AddDomainStyled css={{ width: isMobile ? '300px' : '389px' }}>
      <AddDomainModalInput css={css}>
        <InputWithIcon
          value={inputValue}
          onBlur={validateInput}
          placeholder={t('add_domain.input_placeholder')}
          onChange={handleDomainInput}
          error={showInputError}
        />
      </AddDomainModalInput>
      {isError && <DomainModalError>{getErrorText(error)}</DomainModalError>}
      <AddDomainModalButtonContainer>
        <LargeAuthButton
          css={{ width: isMobile ? '300px' : '389px', marginBottom: '$4' }}
          onClick={addNewDomain}
        >
          {(isLoading || isDeleting || domainSubmitted) && <LoadingIcon />}
          {t('add_domain.continue')}
        </LargeAuthButton>
        {showInputError && <InputError>{inputError}</InputError>}
      </AddDomainModalButtonContainer>
    </AddDomainStyled>
  )
}
